/**
 * "Turn the phone." — a full-screen card shown when a touch device is held upright.
 *
 * ## Why a prompt rather than just laying out for portrait
 *
 * Because a portrait layout is not a worse flight simulator, it is not one at all. The horizon is
 * the widest thing in the frame and everything a pilot flies by is referenced to it; in portrait
 * the useful field of view is about forty degrees wide and the on-screen stick, the throttle and
 * six buttons have to come out of the same 390 px. Every attempt to make that work ends with
 * controls over the instruments.
 *
 * So this is a hard gate, and it is the only one in the product: the touch layer is hidden while it
 * is up (which also releases anything held — see `TouchControls.releaseAll`), and the simulator
 * pauses behind it. Rotating dismisses it; there is no "continue anyway", because there is nothing
 * to continue to.
 *
 * ## Why the viewport and not `screen.orientation`
 *
 * A phone in split-screen or picture-in-picture reports landscape from the sensor while the canvas
 * has a tall sliver of it, and the sliver is what makes the panel unreadable. `shouldPromptForLandscape`
 * in `core/platform.ts` measures the space the canvas actually has.
 *
 * `updateFrame(alpha)`: no. Driven by `resize` and `orientationchange`, which is when the answer
 * can change.
 */

import { PRODUCT_NAME } from '../brand.js';

export interface LandscapePromptOptions {
  /** Told when the prompt appears or goes away, so the loop can pause and the touch layer hide. */
  onChange?: (showing: boolean) => void;
  parent?: HTMLElement;
}

/** The rotate glyph: a phone turning, with an arrow. Inline for the same reason as the camera. */
const ROTATE_SVG =
  '<svg viewBox="0 0 64 44" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<rect x="4" y="2" width="22" height="40" rx="3.5"/>' +
  '<rect x="36" y="12" width="26" height="20" rx="3.5"/>' +
  '<path d="M28 6a12 12 0 0 1 8 6"/>' +
  '<path d="M36 12h-5m5 0V7"/>' +
  '</svg>';

export class LandscapePrompt {
  readonly element: HTMLDivElement;

  private readonly options: LandscapePromptOptions;
  private showingValue = false;

  constructor(options: LandscapePromptOptions = {}) {
    this.options = options;
    this.element = document.createElement('div');
    this.element.className = 'rotate';
    this.element.hidden = true;
    this.element.setAttribute('role', 'status');

    const card = document.createElement('div');
    card.className = 'rotate__card';

    const glyph = document.createElement('div');
    glyph.className = 'rotate__glyph';
    glyph.innerHTML = ROTATE_SVG;

    const title = document.createElement('div');
    title.className = 'rotate__title';
    title.textContent = 'Turn your phone sideways';

    const note = document.createElement('p');
    note.className = 'rotate__note';
    // Says *why*, because "rotate your device" with no reason reads as an arbitrary restriction and
    // the reason here is a good one.
    note.textContent =
      'The horizon is the widest thing in the frame, and it is what you fly by. '
      + `${PRODUCT_NAME} needs the long edge of the screen for it.`;

    card.append(glyph, title, note);
    this.element.append(card);
    (options.parent ?? document.body).append(this.element);
  }

  get showing(): boolean {
    return this.showingValue;
  }

  /** Idempotent, so the resize handler can assert the state rather than track transitions. */
  setShowing(showing: boolean): void {
    if (showing === this.showingValue) return;
    this.showingValue = showing;
    this.element.hidden = !showing;
    this.options.onChange?.(showing);
  }

  dispose(): void {
    this.element.remove();
  }
}
